/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Boris Kudlač (kudlac@cadwork.cz)
 *
 */

import CameraVR from "App/view/cameras/camera_vr";
import View from "App/view/view";
import {mat4, vec3} from "gl-matrix";
import {CameraTypeEnum} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

/**
 *  Camera for VR view mode.
 *
 *  @extends {CameraVR}
 */
export default
class CameraWebVR extends CameraVR {

    private _frameData: VRFrameData = null;

    private _pose: vec3 = vec3.create();
    private _inverseRotationMatrix: mat4 = mat4.create();

    private _offsetedEye: vec3 = vec3.create();
    private _offsetedTarget: vec3 = vec3.create();

    constructor(aView: View) {
        super(aView);
    }

    public getCameraType(): CameraTypeEnum {
        return CameraTypeEnum.WEBVR;
    }

    public setVRDisplay(aVRDisplay: VRDisplay) {
        if (aVRDisplay) {
            aVRDisplay.depthNear = this.znear;
            aVRDisplay.depthFar = this.zfar;
        }
    }

    public setFrameData(aFrameData) {
        this._frameData = aFrameData;
    }

    public getFrameData() {
        return this._frameData;
    }

    public setPose(aPose) {
        this._pose = vec3.clone(aPose);
    }

    public getPose() {
        return vec3.clone(this._pose);
    }

    public setInverseRotationmatrix(aMatrix) {
        this._inverseRotationMatrix = mat4.clone(aMatrix);
    }

    public getViewMatrix(): mat4 {
        if (this._viewMatInvalid) {
            this._recomputeViewMatrix();
            this._viewMatInvalid = false;
        }

        mat4.copy(this._pgc.VMat, this.viewMat);
        return this._pgc.VMat;
    }

    public computeProjectionMatrix(): mat4 {
        if (!this._frameData) {
            return mat4.create();
        }

        if (this._renderingLeftView) {
            return <mat4> this._frameData.leftProjectionMatrix;
        } else if (this._renderingRightView) {
            return <mat4> this._frameData.rightProjectionMatrix;
        }

        return mat4.create();
    }

    protected _recomputeViewMatrix() {
        if (!this._frameData) {
            return;
        }

        if (this._renderingLeftView) {
            this._solveBehaviorInVR(<mat4> this._frameData.leftViewMatrix);
        } else if (this._renderingRightView) {
            this._solveBehaviorInVR(<mat4> this._frameData.rightViewMatrix);
        }
    }

    private _solveBehaviorInVR(aEyeMatrix: mat4) {
        let offset = vec3.fromValues(-aEyeMatrix[12], -aEyeMatrix[13], -aEyeMatrix[14]);
        vec3.transformMat4(offset, offset, this._inverseRotationMatrix);

        vec3.add(this._offsetedEye, this.eye, offset);
        vec3.add(this._offsetedTarget, this.target, offset);

        mat4.lookAt(this.viewMat, this._offsetedEye, this._offsetedTarget, this.upVector);
    }

}
